import { ForensicsSearchAlarms } from '@hakimo-ui/hakimo/types';
import {
  ExclamationTriangleIcon,
  LightBulbIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import type { FC } from 'react';

interface Props {
  isLoading: boolean;
  results: ForensicsSearchAlarms[] | undefined;
}

export const ForensicsMessageSection: FC<Props> = ({ isLoading, results }) => {
  if (isLoading && (results === undefined || results?.length === 0)) {
    return (
      <div className="mt-8 flex flex-col items-center justify-center py-16">
        <div className="relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-primary-600/20 h-24 w-24 animate-ping rounded-full" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-primary-600/30 h-20 w-20 animate-pulse rounded-full" />
          </div>

          <div className="border-primary-600/30 from-primary-600/20 to-primary-600/5 relative flex h-24 w-24 items-center justify-center rounded-full border-4 bg-gradient-to-br backdrop-blur-sm">
            <SparklesIcon className="text-primary-600 h-10 w-10 animate-pulse" />
          </div>
        </div>

        <div className="mt-8 text-center">
          <h3 className="text-onlight-text-1 dark:text-ondark-text-1 mb-2 text-xl font-bold">
            Searching...
          </h3>
          <p className="text-onlight-text-2 dark:text-ondark-text-2 max-w-md text-sm font-medium">
            Analyzing video content and matching your query
          </p>

          <div className="mt-6 flex items-center justify-center gap-2">
            <div className="bg-primary-600 h-2 w-2 animate-bounce rounded-full [animation-delay:-0.3s]" />
            <div className="bg-primary-600 h-2 w-2 animate-bounce rounded-full [animation-delay:-0.15s]" />
            <div className="bg-primary-600 h-2 w-2 animate-bounce rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  if (results?.length === 0) {
    return (
      <div className="mt-8 flex flex-col items-center justify-center py-16">
        <div className="relative mb-8">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-onlight-text-3/10 dark:bg-ondark-text-3/10 h-32 w-32 rounded-full blur-2xl" />
          </div>

          <div className="border-onlight-line-3 bg-onlight-bg-2/50 dark:border-ondark-line-3 dark:bg-ondark-bg-2/50 relative flex h-24 w-24 items-center justify-center rounded-2xl border-2 border-dashed backdrop-blur-sm">
            <MagnifyingGlassIcon className="text-onlight-text-3 dark:text-ondark-text-3 h-12 w-12" />
            <ExclamationTriangleIcon className="absolute -right-2 -top-2 h-8 w-8 rounded-full bg-amber-500/20 p-1.5 text-amber-600 dark:text-amber-400" />
          </div>
        </div>

        <div className="max-w-lg text-center">
          <h3 className="text-onlight-text-1 dark:text-ondark-text-1 mb-3 text-xl font-bold">
            No matching alarms found
          </h3>
          <p className="text-onlight-text-2 dark:text-ondark-text-2 mb-6 text-sm font-medium">
            We couldn't find any alarms matching your search criteria. Try
            adjusting your query or time range.
          </p>

          <div className="border-onlight-line-2 bg-onlight-bg-1/50 dark:border-ondark-line-2 dark:bg-ondark-bg-1/50 mx-auto max-w-md space-y-3 rounded-xl border p-6 backdrop-blur-sm">
            <div className="flex items-center gap-2 text-left">
              <LightBulbIcon className="text-primary-600 h-5 w-5 flex-shrink-0" />
              <p className="text-onlight-text-2 dark:text-ondark-text-2 text-xs font-medium">
                <span className="text-primary-600 font-semibold">Tip:</span> Try
                using different keywords or descriptions
              </p>
            </div>
            <div className="flex items-center gap-2 text-left">
              <LightBulbIcon className="text-primary-600 h-5 w-5 flex-shrink-0" />
              <p className="text-onlight-text-2 dark:text-ondark-text-2 text-xs font-medium">
                <span className="text-primary-600 font-semibold">Tip:</span>{' '}
                Expand your time range to search more data
              </p>
            </div>
            <div className="flex items-center gap-2 text-left">
              <LightBulbIcon className="text-primary-600 h-5 w-5 flex-shrink-0" />
              <p className="text-onlight-text-2 dark:text-ondark-text-2 text-xs font-medium">
                <span className="text-primary-600 font-semibold">Tip:</span> Be
                more specific about colors, objects, or actions
              </p>
            </div>
            <div className="flex items-center gap-2 text-left">
              <LightBulbIcon className="text-primary-600 h-5 w-5 flex-shrink-0" />
              <p className="text-onlight-text-2 dark:text-ondark-text-2 text-xs font-medium">
                <span className="text-primary-600 font-semibold">Tip:</span>{' '}
                Upload an image to instantly find visually similar objects
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
